import moment from 'moment';
import { isValidInterestRate } from '../../utils';
export const useSecurityDepositInterestRateEditableRow = ({
  newInterestRatesToCreate,
  interestRate,
  setNewInterestRatesToCreate,
  alreadyExistingRates,
  setAlreadyExistingRates,
  idx,
  id,
  setModifiedRates,
}) => {
  const isExistingRate = id !== null;
  const isNewRate = idx !== null;

  function handleStartDateChange(date) {
    const dateMinusOneDay = moment(date).subtract(1, 'day');
    if (isNewRate) {
      setNewInterestRatesToCreate((prevRates) => {
        const prevNewInterestRatesToCreateCopy = [...prevRates];
        const lastNewRateIdx = idx - 1; // The most recent "new" rate is the last one
        const lastNewRate = prevRates[lastNewRateIdx];

        // check previous rates for the last "to be created" rate and update its end date if necessary
        if (lastNewRate && lastNewRate.endDate === null) {
          prevNewInterestRatesToCreateCopy[lastNewRateIdx].endDate =
            dateMinusOneDay;
        }

        const lastAlreadyExistingRate = alreadyExistingRates.slice(-1)[0];

        // check previous rates for the last "already existing" rate and update its end date if necessary
        if (
          lastAlreadyExistingRate &&
          lastAlreadyExistingRate.endDate === null
        ) {
          // Update the last already existing rate's end date to the day before the new start date if it's null
          setAlreadyExistingRates((prev) => {
            const prevAlreadyExistingRatesCopy = [...prev];
            const lastAlreadyExistingRateIndex =
              prevAlreadyExistingRatesCopy.findIndex(
                (r) => r.id === lastAlreadyExistingRate.id,
              );

            if (lastAlreadyExistingRateIndex !== -1) {
              prevAlreadyExistingRatesCopy[
                lastAlreadyExistingRateIndex
              ].endDate = dateMinusOneDay;
            }

            // Add the modified existing rate to the modified rates list
            setModifiedRates((prevModifiedRates) => {
              const prevModifiedRatesCopy = [...prevModifiedRates];
              const modifiedExistingRateId = alreadyExistingRates.find(
                (r) => r.id === lastAlreadyExistingRate.id,
              )?.id;
              const modifiedRateIdx = prevModifiedRatesCopy.findIndex(
                (r) => r.id === modifiedExistingRateId,
              );
              if (modifiedRateIdx !== -1) {
                prevModifiedRatesCopy[modifiedRateIdx].endDate =
                  dateMinusOneDay;
              } else {
                prevModifiedRatesCopy.push({
                  id: modifiedExistingRateId,
                  endDate: dateMinusOneDay,
                });
              }
              return prevModifiedRatesCopy;
            });
            return prevAlreadyExistingRatesCopy;
          });
        }

        // update the modified rate
        prevNewInterestRatesToCreateCopy[idx].startDate = date;
        return prevNewInterestRatesToCreateCopy;
      });
      return;
    }

    if (isExistingRate) {
      // No funny end date business for existing rates when updating the start date
      setAlreadyExistingRates((prevRates) => {
        const prevAlreadyExistingRatesCopy = [...prevRates];
        const modifiedExistingRateIdx = prevAlreadyExistingRatesCopy.findIndex(
          (r) => r.id === id,
        );
        prevAlreadyExistingRatesCopy[modifiedExistingRateIdx].startDate = date;
        setModifiedRates((prevRates) => {
          const prevModifiedRatesCopy = [...prevRates];
          const modifiedRateIdx = prevModifiedRatesCopy.findIndex(
            (r) => r.id === id,
          );
          if (modifiedRateIdx !== -1) {
            prevModifiedRatesCopy[modifiedRateIdx].startDate = date;
          } else {
            prevModifiedRatesCopy.push({
              id: interestRate.id,
              startDate: date,
            });
          }
          return prevModifiedRatesCopy;
        });
        return prevAlreadyExistingRatesCopy;
      });
      return;
    }
  }
  function handleEndDateChange(date) {
    if (isNewRate) {
      setNewInterestRatesToCreate((prevRates) => {
        const newRates = [...prevRates];
        newRates[idx].endDate = date;
        return newRates;
      });
      return;
    }

    if (isExistingRate) {
      setAlreadyExistingRates((prevRates) => {
        const prevAlreadyExistingRatesCopy = [...prevRates];
        const modifiedExistingRateIdx = prevAlreadyExistingRatesCopy.findIndex(
          (r) => r.id === id,
        );
        prevAlreadyExistingRatesCopy[modifiedExistingRateIdx].endDate = date;
        setModifiedRates((prevRates) => {
          const prevModifiedRatesCopy = [...prevRates];
          const modifiedRateIdx = prevModifiedRatesCopy.findIndex(
            (r) => r.id === id,
          );
          if (modifiedRateIdx !== -1) {
            prevModifiedRatesCopy[modifiedRateIdx].endDate = date;
          } else {
            prevModifiedRatesCopy.push({
              id: interestRate.id,
              endDate: date,
            });
          }
          return prevModifiedRatesCopy;
        });
        return prevAlreadyExistingRatesCopy;
      });
    }
  }

  function handleInterestRateChange({ target: { value } }) {
    if (isNewRate) {
      setNewInterestRatesToCreate((prevRates) => {
        const newRates = [...prevRates];
        newRates[idx].interest = value;
        return newRates;
      });
      return;
    }

    if (isExistingRate) {
      setAlreadyExistingRates((prevRates) => {
        const prevAlreadyExistingRatesCopy = [...prevRates];
        const modifiedExistingRateIdx = prevAlreadyExistingRatesCopy.findIndex(
          (r) => r.id === id,
        );
        prevAlreadyExistingRatesCopy[modifiedExistingRateIdx].interest = value;
        setModifiedRates((prevRates) => {
          const prevModifiedRatesCopy = [...prevRates];
          const modifiedRateIdx = prevModifiedRatesCopy.findIndex(
            (r) => r.id === id,
          );
          if (modifiedRateIdx !== -1) {
            prevModifiedRatesCopy[modifiedRateIdx].interest = value;
          } else {
            prevModifiedRatesCopy.push({
              id: interestRate.id,
              interest: value,
            });
          }
          return prevModifiedRatesCopy;
        });
        return prevAlreadyExistingRatesCopy;
      });
    }
  }

  function shouldDisableStartDate(date) {
    const isNotFirstDayOfMonth = date.date() !== 1;
    const isAfterEndDate = date.isAfter(interestRate.endDate);
    const newInterestRatesToCreateBeforeCurrent =
      newInterestRatesToCreate.slice(0, idx);
    const isSameOrBeforePreviousRateStartDate =
      newInterestRatesToCreateBeforeCurrent.find((r) => {
        return (
          date.isSameOrBefore(r.startDate) ||
          (r.endDate && date.isSameOrBefore(r.endDate))
        );
      }) !== undefined ||
      alreadyExistingRates.find((r) => {
        return (
          date.isSameOrBefore(r.startDate) ||
          (r.endDate && date.isSameOrBefore(r.endDate))
        );
      }) !== undefined;

    const isBeforeFirstOfCurrentMonth = date.isBefore(
      moment().startOf('month'),
    );
    return (
      isNotFirstDayOfMonth ||
      isAfterEndDate ||
      isBeforeFirstOfCurrentMonth ||
      isSameOrBeforePreviousRateStartDate
    );
  }

  function shouldDisableEndDate(date) {
    const dateIsMoreThanOneMonthBeforeCurrentMonth = moment(date).isBefore(
      moment().startOf('month').subtract(1, 'month'),
    );
    const newInterestRatesToCreateAfterCurrent = newInterestRatesToCreate.slice(
      idx + 1,
    );
    const isAfterEndDateOfOtherRates =
      newInterestRatesToCreateAfterCurrent.find((r) => {
        return (
          date.isAfter(r.startDate) || (r.endDate && date.isAfter(r.endDate))
        );
      }) !== undefined;

    return (
      date.date() !== moment(date).endOf('month').date() ||
      date.isBefore(interestRate.startDate) ||
      dateIsMoreThanOneMonthBeforeCurrentMonth ||
      isAfterEndDateOfOtherRates
    );
  }

  const endDateIsNotNull = interestRate.endDate !== null;
  const endDateOrInterestRateIsNotNull =
    endDateIsNotNull || isValidInterestRate(interestRate.interest);
  const startDateIsNotNull = !!interestRate.startDate;
  const endDateOrStartDateIsNotNull = endDateIsNotNull || startDateIsNotNull;
  const startDateInvalid =
    endDateOrInterestRateIsNotNull && !interestRate.startDate;
  const interestRateInvalid =
    endDateOrStartDateIsNotNull && !isValidInterestRate(interestRate.interest);

  return {
    handleStartDateChange,
    handleEndDateChange,
    handleInterestRateChange,
    shouldDisableStartDate,
    shouldDisableEndDate,
    startDateInvalid,
    interestRateInvalid,
  };
};
